import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, CopyIcon, ExternalLinkIcon, SendIcon, XIcon } from 'lucide-react';
import type { WorkflowActivityInput } from '../../lib/workflow';
import { ACTIVITY_TYPES, getActivityTypeForChannel, type ActivityMessageChannel, type ActivityType } from '../../lib/activities/types';
type ContactChannel = ActivityMessageChannel | 'manual';
type WorkflowActionModalProps = {
  actionType: string;
  actionLabel: string;
  businessName: string;
  initialChannel?: ContactChannel;
  channelUrl?: string | null;
  messageTemplate?: string;
  onClose: () => void;
  onConfirm: (input: WorkflowActivityInput) => Promise<void>;
};
const responseOptions = [{
  value: 'Pidió precio',
  interest: 'hot'
}, {
  value: 'Quiere información',
  interest: 'warm'
}, {
  value: 'No le interesa',
  interest: 'cold'
}, {
  value: 'Quiere llamada',
  interest: 'hot'
}, {
  value: 'No respondió',
  interest: 'cold'
}, {
  value: 'Otro',
  interest: ''
}];
const activityByAction: Record<string, ActivityType> = {
  wait_for_response: ACTIVITY_TYPES.RESPONSE_RECEIVED,
  respond: ACTIVITY_TYPES.RESPONSE_RECEIVED,
  qualify: ACTIVITY_TYPES.RESPONSE_RECEIVED,
  schedule_meeting: ACTIVITY_TYPES.MEETING,
  hold_meeting: ACTIVITY_TYPES.MEETING,
  send_proposal: ACTIVITY_TYPES.PROPOSAL_SENT,
  negotiate: ACTIVITY_TYPES.OTHER,
  nurture: ACTIVITY_TYPES.FOLLOW_UP,
  close_lost: ACTIVITY_TYPES.OTHER
};
export function WorkflowActionModal({
  actionType,
  actionLabel,
  businessName,
  initialChannel = 'manual',
  channelUrl,
  messageTemplate = '',
  onClose,
  onConfirm
}: WorkflowActionModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isMessageAction = ['send_message', 'follow_up'].includes(actionType);
  const isResponseAction = ['wait_for_response', 'respond', 'qualify'].includes(actionType);
  const [description, setDescription] = useState(messageTemplate || defaultDescription(actionType, businessName));
  const [channel, setChannel] = useState<ContactChannel>(initialChannel);
  const [outcome, setOutcome] = useState(responseOptions[1].value);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    closeButtonRef.current?.focus();
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isSaving, onClose]);
  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(description);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('No se pudo copiar el mensaje. Podés seleccionarlo manualmente.');
    }
  }
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isMessageAction && !confirmed) {
      setError('Confirmá que el mensaje fue enviado antes de registrarlo.');
      return;
    }
    const outcomeOption = responseOptions.find(item => item.value === outcome);
    const safeDescription = typeof description === 'string' ? description.trim() : '';
    const finalDescription = isResponseAction ? [outcome, note.trim()].filter(Boolean).join('. ') : safeDescription;
    if (!finalDescription) {
      setError('Agregá una nota breve antes de registrar la acción.');
      return;
    }
    const messageActivityType = isMessageAction ? getActivityTypeForChannel(channel) : null;
    if (isMessageAction && !messageActivityType) {
      setError('Elegí WhatsApp, Instagram, Facebook o Correo para registrar el envío.');
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await onConfirm({
        activityType: messageActivityType ?? activityByAction[actionType] ?? ACTIVITY_TYPES.OTHER,
        description: finalDescription,
        channel: isMessageAction ? channel as ActivityMessageChannel : undefined,
        result: isMessageAction ? 'sent' : isResponseAction ? outcome : undefined,
        receivedResponse: isMessageAction ? false : isResponseAction,
        interestLevel: outcomeOption?.interest || undefined
      });
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'No se pudo registrar la acción.');
    } finally {
      setIsSaving(false);
    }
  }
  return <div className="fixed inset-0 z-50 flex items-end bg-slate-950/75 sm:items-center sm:justify-center sm:p-6" data-id="element-617">
      <section aria-labelledby="workflow-action-title" aria-modal="true" className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-white/10 bg-[#0b1728] p-5 shadow-2xl sm:max-w-xl sm:rounded-2xl sm:p-7" role="dialog" data-id="element-618">
        <div className="mb-6 flex items-start justify-between gap-4" data-id="element-619">
          <div data-id="element-620">
            <p className="font-orbitron text-xs font-bold tracking-[.16em] text-cyan-200" data-id="element-621">
              GUÍA DE VENTA
            </p>
            <h2 className="mt-1 text-xl font-bold text-white" id="workflow-action-title" data-id="element-622">
              {isResponseAction ? 'Registrar respuesta' : actionLabel}
            </h2>
            <p className="mt-1 text-sm text-slate-400" data-id="element-623">{businessName}</p>
          </div>
          <button ref={closeButtonRef} aria-label="Cerrar" className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" disabled={isSaving} onClick={onClose} type="button" data-id="element-624">
            <XIcon className="h-5 w-5" data-id="element-625" />
          </button>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit} data-id="element-626">
          {isMessageAction ? <>
              <fieldset data-id="element-627">
                <legend className="mb-2 text-sm font-medium text-slate-200" data-id="element-628">
                  Canal recomendado
                </legend>
                <div className="grid grid-cols-2 gap-2" data-id="element-629">
                  {([['whatsapp', 'WhatsApp'], ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['email', 'Correo']] as Array<[ContactChannel, string]>).map(([value, label]) => <label className={`cursor-pointer rounded-xl border px-3 py-2.5 text-sm font-semibold ${channel === value ? 'border-cyan-300/60 bg-cyan-300/10 text-cyan-100' : 'border-white/10 text-slate-300 hover:bg-white/5'}`} key={value} data-id="element-630">
                      <input checked={channel === value} className="sr-only" name="channel" onChange={() => setChannel(value)} type="radio" value={value} data-id="element-631" />
                      {label}
                    </label>)}
                </div>
              </fieldset>

              <div data-id="element-632">
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-633">
                  <label className="text-sm font-medium text-slate-200" htmlFor="sales-message" data-id="element-634">
                    Mensaje sugerido editable
                  </label>
                  <div className="flex gap-2" data-id="element-635">
                    <button className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-sm font-semibold text-slate-200 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" onClick={() => void copyMessage()} type="button" data-id="element-636">
                      {copied ? <CheckIcon className="h-4 w-4 text-emerald-300" data-id="element-637" /> : <CopyIcon className="h-4 w-4" data-id="element-638" />}
                      {copied ? 'Copiado' : 'Copiar'}
                    </button>
                    {channelUrl ? <a className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300/30 px-2.5 py-1.5 text-sm font-semibold text-cyan-100 hover:bg-cyan-300/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" href={channelUrl} rel="noreferrer" target="_blank" data-id="element-639">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-640" />
                        Abrir canal
                      </a> : null}
                  </div>
                </div>
                <textarea className="mt-2 min-h-36 w-full resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-3 text-sm leading-6 text-white outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20" id="sales-message" onChange={event => setDescription(event.target.value)} value={description} data-id="element-641" />
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-amber-300/20 bg-amber-300/10 p-3 text-sm text-amber-50" data-id="element-642">
                <input checked={confirmed} className="mt-0.5 h-4 w-4 accent-cyan-300" onChange={event => setConfirmed(event.target.checked)} type="checkbox" data-id="element-643" />
                Confirmo que el mensaje fue enviado.
              </label>
            </> : null}

          {isResponseAction ? <>
              <fieldset data-id="element-644">
                <legend className="text-sm font-medium text-slate-200" data-id="element-645">
                  ¿Qué respondió?
                </legend>
                <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-646">
                  {responseOptions.map(item => <label className={`cursor-pointer rounded-xl border px-3 py-2.5 text-sm font-semibold ${outcome === item.value ? 'border-cyan-300/60 bg-cyan-300/10 text-cyan-100' : 'border-white/10 text-slate-300 hover:bg-white/5'}`} key={item.value} data-id="element-647">
                      <input checked={outcome === item.value} className="sr-only" name="outcome" onChange={() => setOutcome(item.value)} type="radio" value={item.value} data-id="element-648" />
                      {item.value}
                    </label>)}
                </div>
              </fieldset>
              <label className="block text-sm font-medium text-slate-200" data-id="element-649">
                Contexto breve{' '}
                <span className="text-slate-500" data-id="element-650">(opcional)</span>
                <textarea className="mt-2 min-h-24 w-full resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-3 text-sm text-white outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20" onChange={event => setNote(event.target.value)} placeholder="Añadí un dato útil para el siguiente paso." value={note} data-id="element-651" />
              </label>
            </> : null}

          {!isMessageAction && !isResponseAction ? <label className="block text-sm font-medium text-slate-200" data-id="element-652">
              Nota de la acción
              <textarea className="mt-2 min-h-28 w-full resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-3 text-sm text-white outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20" onChange={event => setDescription(event.target.value)} value={description} data-id="element-653" />
            </label> : null}

          {error ? <p className="rounded-lg border border-rose-400/20 bg-rose-400/10 p-3 text-sm text-rose-100" role="alert" data-id="element-654">
              {error}
            </p> : null}

          <div className="flex flex-col-reverse gap-3 border-t border-white/10 pt-5 sm:flex-row sm:justify-end" data-id="element-655">
            <button className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5" disabled={isSaving} onClick={onClose} type="button" data-id="element-656">
              Cancelar
            </button>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 py-2.5 text-sm font-bold text-[#06202a] hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-60" disabled={isSaving} type="submit" data-id="element-657">
              <SendIcon className="h-4 w-4" data-id="element-658" />
              {isSaving ? 'Guardando…' : isResponseAction ? 'Registrar respuesta' : isMessageAction ? 'Marcar mensaje enviado' : 'Registrar acción'}
            </button>
          </div>
        </form>
      </section>
    </div>;
}
function defaultDescription(actionType: string, businessName: string) {
  if (actionType === 'send_message') {
    return `Hola, soy [tu nombre]. Vi ${businessName} y quería compartirte una idea concreta para ayudarles con [beneficio relevante]. ¿Te parece si te envío la información por aquí?`;
  }
  return '';
}